import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Menu,
  X,
  LayoutDashboard,
  Monitor,
  ClipboardList,
  BarChart3,
  PieChart,
  Server,
  HardDrive,
  Key,
  Puzzle,
  Package,
  Box,
  Layers,
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Search,
  Bell,
  Plus,
  Moon,
  Grid3x3,
  Download,
  FileSpreadsheet,
  Laptop,
  DoorOpen,
  MessageSquare,
  Clock,
  Users,
  Briefcase,
  Activity,
  Target,
  Shield,
  FileText,
  Radio,
  UserCheck,
  UserX,
  CloudUpload,
  Settings,
  Building2,
  Smartphone,
  Wrench,
  SlidersHorizontal,
  UsersRound,
  Recycle,
  GraduationCap,
  CreditCard,
  Palette,
  LifeBuoy,
  Bug,
  Gauge,
  TrendingUp,
  Paintbrush
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { cn } from "../../lib/utils";
import { portfolioData } from "../../mock";

const sidebarTeal = "bg-[#006d77]";
const sidebarTealHover = "hover:bg-white/10";
const topBar = "bg-[#141e33]";

function NavItem({ icon: Icon, label, active }) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] leading-snug transition-colors",
        active ? "bg-white/20 text-white font-medium" : "text-white/90 " + sidebarTealHover
      )}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-95" strokeWidth={1.75} />
      <span>{label}</span>
    </button>
  );
}

function NavGroup({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-1">
      <CollapsibleTrigger
        className={cn(
          "flex w-full items-center gap-2 rounded-l-lg px-2 py-2.5 text-left text-[13px] font-medium text-white transition-colors",
          open ? "bg-white/12" : "bg-white/5",
          sidebarTealHover
        )}
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        <span className="flex-1">{title}</span>
        {open ? <ChevronUp className="h-4 w-4 opacity-80" /> : <ChevronDown className="h-4 w-4 opacity-80" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-0.5 border-l border-white/10 py-1 pl-3 ml-2 mt-0.5">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

const focusKpis = [
  { label: "Problematic devices", value: "1", bar: "bg-rose-500" },
  { label: "Need maintenance", value: "31", bar: "bg-amber-400" },
  { label: "Healthy ratio", value: "96.8%", bar: "bg-emerald-500" },
  { label: "Total assets", value: "607", bar: "bg-sky-500" }
];

const snapshotCards = [
  { value: "607", label: "Assets" },
  { value: "12", label: "Licenses" },
  { value: "107", label: "Accessories" },
  { value: "70", label: "Components" },
  { value: "413", label: "Users" }
];

function SidebarNav({ onCloseMobile }) {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-3">
        {onCloseMobile && (
          <button
            type="button"
            className="rounded p-1 hover:bg-white/10 md:hidden"
            onClick={onCloseMobile}
            aria-label="Tutup menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <button type="button" className="hidden rounded p-1 hover:bg-white/10 md:block" aria-label="Menu">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white text-[#006d77] font-bold text-sm shadow-sm">
          W
        </div>
        <span className="text-lg font-semibold tracking-tight">WorkHub</span>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3 space-y-1">
        <NavItem icon={LayoutDashboard} label="Dashboard" active />

        <NavGroup title="Device Assessment" icon={ClipboardList} defaultOpen>
          <NavItem icon={Monitor} label="Manage Devices" />
          <NavItem icon={ClipboardList} label="Manage Assessments" />
          <NavItem icon={BarChart3} label="Assessment Summary" />
          <NavItem icon={PieChart} label="Assessment Analysis" />
        </NavGroup>

        <NavGroup title="Assets Management" icon={Server} defaultOpen>
          <NavItem icon={HardDrive} label="Endpoint Management" />
          <NavItem icon={Box} label="Assets" />
          <NavItem icon={Key} label="Licenses" />
          <NavItem icon={Puzzle} label="Accessories" />
          <NavItem icon={Package} label="Consumables" />
          <NavItem icon={Layers} label="Components" />
          <NavItem icon={ShoppingBag} label="Requestable Items" />
        </NavGroup>

        <NavGroup title="Requests Tools" icon={LifeBuoy} defaultOpen={false}>
          <NavItem icon={Gauge} label="Overview" />
          <NavItem icon={Layers} label="PO User Request" />
          <NavItem icon={CreditCard} label="Request Finance" />
          <NavItem icon={Plus} label="Add Menu" />
        </NavGroup>

        <NavGroup title="Ticketing Tools" icon={LifeBuoy} defaultOpen={false}>
          <NavItem icon={Gauge} label="Overview IT Trouble/Ticket" />
          <NavItem icon={TrendingUp} label="IT Team Performance" />
          <NavItem icon={Bug} label="IT Trouble/Ticket" />
          <NavItem icon={Laptop} label="Borrow Device/Accessories" />
          <NavItem icon={Grid3x3} label="Office 365 Request" />
          <NavItem icon={Paintbrush} label="Adobe Request" />
        </NavGroup>

        <NavGroup title="IT Access" icon={DoorOpen} defaultOpen>
          <NavItem icon={LayoutDashboard} label="Dashboard" />
          <NavItem icon={MessageSquare} label="Feedback Data" />
          <NavItem icon={DoorOpen} label="Account IT" />
          <NavItem icon={Clock} label="Shift IT" />
          <NavItem icon={Users} label="Account All Dept" />
          <NavItem icon={Briefcase} label="IT Team Routine" />
          <NavItem icon={Activity} label="Project Tracker" />
        </NavGroup>

        <NavGroup title="Infra Alert" icon={Radio} defaultOpen>
          <NavItem icon={Gauge} label="Control Center" />
          <NavItem icon={Target} label="Targets" />
          <NavItem icon={Shield} label="Diagnostics" />
          <NavItem icon={FileText} label="Policy" />
          <NavItem icon={BarChart3} label="Reports" />
        </NavGroup>

        <NavGroup title="People" icon={UsersRound} defaultOpen={false}>
          <NavItem icon={Users} label="List All" />
          <NavItem icon={Shield} label="Superusers" />
          <NavItem icon={UserCheck} label="Admin Users" />
          <NavItem icon={UserX} label="Deleted Users" />
          <NavItem icon={UserCheck} label="Login Enabled" />
          <NavItem icon={UserX} label="Login Disabled" />
          <NavItem icon={CloudUpload} label="Import" />
        </NavGroup>

        <NavGroup title="Settings" icon={Settings} defaultOpen={false}>
          <NavItem icon={SlidersHorizontal} label="Admin Overview" />
          <NavItem icon={UsersRound} label="Groups" />
          <NavItem icon={Building2} label="Departments" />
          <NavItem icon={Smartphone} label="Manage Devices" />
          <NavItem icon={ClipboardList} label="Manage Assessments" />
          <NavItem icon={CloudUpload} label="Import" />
          <NavItem icon={Wrench} label="Management Settings" />
        </NavGroup>

        <NavGroup title="On & Off Boarding Tools" icon={Briefcase} defaultOpen={false}>
          <NavItem icon={Gauge} label="On & Off Boarding Dashboard" />
          <NavItem icon={ClipboardList} label="Hibob Task" />
          <NavItem icon={CreditCard} label="Intern On & Off Boarding" />
        </NavGroup>

        <NavGroup title="E-Waste Management" icon={Recycle} defaultOpen={false}>
          <NavItem icon={Recycle} label="Overview" />
        </NavGroup>

        <NavGroup title="IT Course" icon={GraduationCap} defaultOpen={false}>
          <NavItem icon={GraduationCap} label="Courses" />
        </NavGroup>

        <p className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/45">
          Other department
        </p>
        <NavGroup title="CSR" icon={Users} defaultOpen={false}>
          <NavItem icon={FileText} label="Reports" />
        </NavGroup>
        <NavGroup title="Finance Tools" icon={CreditCard} defaultOpen={false}>
          <NavItem icon={BarChart3} label="Overview" />
        </NavGroup>
        <NavGroup title="Design Department" icon={Paintbrush} defaultOpen={false}>
          <NavItem icon={Layers} label="Assets" />
        </NavGroup>
        <NavGroup title="K3 Report" icon={Shield} defaultOpen={false}>
          <NavItem icon={FileText} label="Summary" />
        </NavGroup>
      </nav>
    </>
  );
}

export default function WorkhubDemo() {
  const [mobileMenu, setMobileMenu] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#eef2f6] text-slate-800">
      {/* Bar kembali ke portofolio */}
      <header className="shrink-0 z-[60] flex items-center gap-3 border-b border-slate-800 bg-slate-950 px-4 py-2.5">
        <Link
          to="/#projects"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to portfolio
        </Link>
        <span className="text-slate-600">|</span>
        <span className="text-xs text-slate-500">WorkHub demo — sample UI</span>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Mobile overlay sidebar */}
        {mobileMenu && (
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            aria-label="Tutup menu"
            onClick={() => setMobileMenu(false)}
          />
        )}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex w-[min(100vw-3rem,280px)] flex-col overflow-hidden text-white shadow-xl transition-transform duration-200 md:static md:z-auto md:h-auto md:min-h-0 md:w-[260px] lg:w-[272px] md:shrink-0 md:translate-x-0 md:shadow-xl md:self-stretch",
            sidebarTeal,
            mobileMenu ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}
        >
          <SidebarNav onCloseMobile={() => setMobileMenu(false)} />
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar */}
          <div className={cn("flex shrink-0 flex-wrap items-center gap-3 px-4 py-2.5 text-white", topBar)}>
            <button
              type="button"
              className="rounded-lg p-2 hover:bg-white/10 md:hidden"
              aria-label="Buka menu"
              onClick={() => setMobileMenu(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="relative min-w-[200px] flex-1 max-w-xl">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                readOnly
                placeholder="Search all menus/data"
                className="w-full rounded-lg border border-slate-600/80 bg-slate-800/80 py-2 pl-10 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500/50"
              />
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300">ID</span>
              <button type="button" className="rounded-lg p-2 hover:bg-white/10" aria-label="Notifikasi">
                <Bell className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1.5 text-xs hover:bg-white/10"
              >
                Create New
                <ChevronDown className="h-3 w-3" />
              </button>
              <button
                type="button"
                className="flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1.5 text-xs hover:bg-white/10"
              >
                Assessment Tools
                <ChevronDown className="h-3 w-3" />
              </button>
              <div className="flex items-center gap-2 pl-1 border-l border-slate-600 ml-1">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-400 to-teal-500 text-xs font-bold flex items-center justify-center text-slate-900">
                  {portfolioData.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <span className="hidden sm:inline text-sm text-slate-200 max-w-[120px] truncate">
                  {portfolioData.name}
                </span>
              </div>
              <button
                type="button"
                className="rounded-lg px-2 py-1.5 text-xs font-medium text-rose-300 hover:bg-rose-500/10"
              >
                Logout
              </button>
              <button type="button" className="rounded-lg p-2 hover:bg-white/10" aria-label="Dark mode">
                <Moon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Dashboard content */}
          <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            {/* Overview card */}
            <section className="mb-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-200/60">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Overview</p>
              <h1 className="mt-1 font-serif text-3xl sm:text-4xl font-bold text-slate-900">Dashboard</h1>
              <p className="mt-2 text-sm text-slate-600">Summary of device health and issue trends.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200/60">
                  Healthy <strong className="text-emerald-900">30</strong>
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 ring-1 ring-amber-200/60">
                  Need attention <strong>1</strong>
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                  Unknown <strong>0</strong>
                </span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <Laptop className="h-4 w-4" />
                  View Devices
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Export Report
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-sky-900/20 hover:bg-sky-500"
                >
                  <Download className="h-4 w-4" />
                  Download charts
                </button>
              </div>
            </section>

            {/* Today&apos;s Focus */}
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Today&apos;s Focus</h2>
              <p className="text-sm text-slate-500">Quick snapshot for operations teams.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {focusKpis.map((k) => (
                  <div
                    key={k.label}
                    className="flex overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm"
                  >
                    <div className={cn("w-1.5 shrink-0", k.bar)} />
                    <div className="min-w-0 flex-1 p-4">
                      <p className="text-xs font-medium text-slate-500">{k.label}</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{k.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Workspace Navigation */}
            <section className="mb-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Workspace Navigation</h2>
              <p className="text-sm text-slate-600">Quick access to main modules. Hide when not needed.</p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-6 py-3 text-sm font-medium text-slate-800 shadow-inner hover:bg-slate-100"
                >
                  <Grid3x3 className="h-4 w-4" />
                  Main Menu Overview
                </button>
                <div className="relative flex-1 max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    readOnly
                    placeholder="Search All Menus"
                    className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30"
                  />
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500">This block changes less often. Click to show/hide.</p>
            </section>

            {/* Operational Snapshot */}
            <section>
              <h2 className="text-lg font-semibold text-slate-900">Operational Snapshot</h2>
              <p className="text-sm text-slate-600">Primary inventory and active-user KPIs.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {snapshotCards.map((c) => (
                  <div
                    key={c.label}
                    className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/50"
                  >
                    <p className="text-3xl font-bold tracking-tight text-slate-900">{c.value}</p>
                    <p className="mt-1 text-sm font-medium text-slate-600">{c.label}</p>
                    <button
                      type="button"
                      className="mt-4 w-full rounded-lg bg-slate-100 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-200"
                    >
                      View all
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <footer className="mt-10 border-t border-slate-200 pt-6 text-center text-[11px] text-slate-500">
              Tampilan demo portofolio — tidak terhubung ke sistem produksi WorkHub.
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}
